// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingsByRange.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * SettingsByRangeItem component
 */
import { action } from 'mobx';
import React, { Component } from 'react';
import { assign, filter, find, get, getOr, isEqual, isFunction, isNil } from 'lodash/fp';

import SettingsByRangeItemFieldModel from 'logic/models/Izenda.SettingsByRangeItemFieldModel';
import ReportPartUtils from 'logic/Izenda.ReportDesignerCommonDataUtils';
import Constants from 'logic/data/Izenda.SettingsByRangeConstants';

import Validator from 'share/components/hoc/Izenda.Validator';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import DropDown from 'share/components/controls/Izenda.DropDown';

import AddFieldSetting from './Izenda.AddFieldSetting';
import connect from 'share/enhancers/Izenda.Connect';
import actions from './Izenda.SettingsByRangeItemAction';

const ValidateAddFieldSetting = Validator()(AddFieldSetting);
const ValidateInputByDataType = Validator()(InputByDataType);
const ValidateDropDown = Validator()(DropDown);
const DATA_TYPE = Constants.DATA_TYPE;
const THRESHOLD_TYPES = Constants.THRESHOLD.THRESHOLD_TYPES;

@connect(actions)
export default class SettingsByRangeItem extends Component {
  state = {
    overHandle: false
  };

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.setState({
      overHandle: false
    });
    this.loadThresholdFieldFunctions();
  }

  /**
   * Load functions for subtotal global threshold
   */
  @action
  loadThresholdFieldFunctions() {
    const {props} = this;
    const {updateFieldFunctions, configField, itemData} = props;

    const isStatic = itemData.thresholdType === THRESHOLD_TYPES.STATIC.value;
    const isDynamic = itemData.thresholdType === THRESHOLD_TYPES.DYNAMIC.value;
    const isDynamicGlobal = itemData.thresholdType === THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value;

    // No need to load function for static thresholds
    if (!itemData.thresholdType || isStatic)
      return;

    const itemDataField = getOr(null, 'fields[0]', itemData);
    if (!itemDataField)
      return Promise.resolve();

    // Get functions for current data type
    let dataType = null;
    if (isDynamicGlobal) {
      dataType = configField.fieldDataType;
    } else if (isDynamic) {
      const field = this.findFieldInDataTree(itemDataField.fieldId, itemDataField.querySourceId);
      dataType = field && field.field.izendaDataType;
    }

    return new Promise(resolve => {
      // Get functions:
      const onThresholdFieldFunctionsLoaded = data => {
        if (isDynamicGlobal && !itemData.functionId) {
          itemData.functionId = configField.functionData && configField.functionData.id;
        }
        if (isDynamic) {
          itemData.functionId = itemDataField.functionId;
        }

        itemData.functions = filter({useInSubTotal: true}, data);
        itemData.functionData = find({id: itemData.functionId}, itemData.functions);
        updateFieldFunctions(itemData.functions);
        resolve();
      };
      if (dataType) {
        ReportPartUtils.getFunction(0, dataType).then(onThresholdFieldFunctionsLoaded);
      } else {
        onThresholdFieldFunctionsLoaded([]);
      }
    });
  }

  findFieldInDataTree(fieldId, fieldQuerySourceId) {
    const { props } = this;
    const { dataTree } = props;
    return find(
      f => f.id === fieldId && f.querySourceDetail.id === fieldQuerySourceId,
      dataTree);
  };

  render() {
    const {connectDragSource} = this.props;
    const isDraggableEnabled = !isNil(connectDragSource);
    const {overHandle} = this.state;

    return (isDraggableEnabled && overHandle)
      ? connectDragSource(this.renderComponent())
      : this.renderComponent();
  }

  /**
   * Render column type of threshold ( dynamic - static)
   */
  renderType() {
    const {props} = this;
    const {itemData} = props;
    const onUpdateThresholdType = (thresholdObject) => {
      this.props.updateThresholdType(thresholdObject);
      this.loadThresholdFieldFunctions()
    };

    return (
      <DropDown
        labelField='label'
        valueField='value'
        options={[THRESHOLD_TYPES.STATIC, THRESHOLD_TYPES.DYNAMIC, THRESHOLD_TYPES.DYNAMIC_SUBTOTAL]}
        value={itemData.thresholdType}
        onChange={onUpdateThresholdType}
      />
    );
  }

  renderComponent() {
    const {itemData, onDelete, canMove, canChangeThresholdType} = this.props;
    const {isOver} = this.props; // is dragging over itself

    // For validation
    this.validateFromCtrlId = `${itemData.id}_from`;
    this.validateToCtrlId = `${itemData.id}_to`;
    this.validateValueCtrlId = `${itemData.id}_value`;
    this.validateNameCtrlId = `${itemData.id}_name`;

    const hoverStyle = {
      opacity: isOver ? 0 : 1,
    };
    return (
      <tr style={hoverStyle}>
        {canMove && (
          <td className="u_alignMiddle"
              onMouseEnter={() => this.setState({overHandle: true})}
              onMouseLeave={() => this.setState({overHandle: false})}>
            <span className="icon-drag-handle"/>
          </td>
        )}
        {/* Render static/dynamic dropdown */}
        {canChangeThresholdType && (
          <td className="u_alignMiddle">{this.renderType()}</td>
        )}
        {this.renderName()}
        <td className="u_alignMiddle">{this.renderRangeCtrl()}</td>
        <td className="u_alignMiddle">{this.renderValueCtrl(this.validateValueCtrlId)}</td>
        {itemData.canDelete && (
          <td className="u_alignMiddle">
            <span onClick={onDelete} className="icon-cancel"/>
          </td>
        )}
      </tr>);
  }

  renderName() {
    const {itemData, valueType, haveName} = this.props;

    const isEditName = haveName && (valueType === 'rangeValue' || valueType === 'rangePercent');

    const nameProps = {
      type: 'text',
      value: itemData.name,
      validations: Constants.CONSTRAINTS,
      field: this.validateNameCtrlId,
      onChange: handleChange
    };

    const name = isEditName ? <InputByDataType {...nameProps} /> : <span>{itemData.name}</span>;

    return haveName ? <td>{name}</td> : null;

    function handleChange(newVal) {
      itemData.name = newVal.value;
    }
  }

  /**
   * Render value input for single value range.
   */
  renderRangeSingleValue() {
    const {itemData, dataType, dateTimeFormatData} = this.props;
    if (this.isThresholdDynamic(itemData.thresholdType)) {
      // Render input field + function for dynamic threshold
      return this.renderInputDynamicThresholdCtrl(itemData.fields[0]);

    } else if (this.isThresholdDynamicSubtotal(itemData.thresholdType)) {
      // Render function for dynamic subtotal threshold
      return this.renderInputDynamicSubtotalThresholdCtrl(itemData.fields[0], itemData.id);

    } else {
      const inputType = this.getInputTypeByDataType(dataType, itemData.forcedDataType);
      const dateTimeFormatDataResult = this.getDateTimeFormatData(dateTimeFormatData);
      const inputControlProps = {
        groupByDateTime: get('groupBy', dateTimeFormatDataResult)
      };
      const actualType = isEqual(itemData.name, 'High') || isEqual(itemData.name, 'Low') ? 'number' : inputType;
      return this.renderInputCtrl(actualType, itemData, 'key', itemData.id, inputControlProps)
    }
  }

  /**
   * Render value input for all values.
   */
  renderRangeAllValues() {
    return (<label>All Values</label>)
  }

  /**
   * Render value input for two value range.
   */
  renderRangeValue() {
    const {itemData, dataType, dateTimeFormatData} = this.props;
    const inputType = this.getInputTypeByDataType(dataType);
    const dateTimeFormatDataResult = this.getDateTimeFormatData(dateTimeFormatData);
    const inputControlProps = {
      groupByDateTime: get('groupBy', dateTimeFormatDataResult)
    };

    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols={5}>
            {this.renderInputCtrl(inputType, itemData, 'from', this.validateFromCtrlId, inputControlProps)}
          </GridLayout.Col>
          <GridLayout.Col cols={2}>
            <span>-</span>
          </GridLayout.Col>
          <GridLayout.Col cols={5}>
            {this.renderInputCtrl(inputType, itemData, 'to', this.validateToCtrlId, inputControlProps)}
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    )
  }

  /**
   * Render value input for two value percent range.
   */
  renderRangePercentValue() {
    const {itemData, dataType, dateTimeFormatData} = this.props;
    const inputType = this.getInputTypeByDataType(dataType);
    const dateTimeFormatDataResult = this.getDateTimeFormatData(dateTimeFormatData);
    const inputControlProps = {
      groupByDateTime: get('groupBy', dateTimeFormatDataResult)
    };
    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols={5}>
            <div className="input-group">
              {this.renderInputCtrl(inputType, itemData, 'from', this.validateFromCtrlId, inputControlProps)}
              <span className="input-group-addon">%</span>
            </div>
          </GridLayout.Col>
          <GridLayout.Col cols={2} className="u_controlHeight">
            <span>-</span>
          </GridLayout.Col>
          <GridLayout.Col cols={5}>
            <div className="input-group">
              {this.renderInputCtrl(inputType, itemData, 'to', this.validateToCtrlId, inputControlProps)}
              <span className="input-group-addon">%</span>
            </div>
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }

  /**
   * Render value control which contains range value or "from-to" values.
   */
  renderRangeCtrl() {
    const {valueType} = this.props;

    switch (valueType) {
      case 'value':
        return this.renderRangeSingleValue();
      case 'rangeValue':
        return this.renderRangeValue();
      case 'rangePercent':
        return this.renderRangePercentValue();
      case 'allValues':
        return this.renderRangeAllValues();
      default:
      // do nothing
    }
  }

  renderValueCtrl(validateId) {
    const {itemData} = this.props;

    const valueProps = this.props.valueProps || {};

    return this.renderInputCtrl(
      valueProps.type,
      itemData,
      'text',
      validateId,
      assign({className: 'value-container'}, valueProps)
    );
  }

  // Field has chosen handler.
  @action
  onInputFieldChanged(fieldModel: SettingsByRangeItemFieldModel, changesData) {
    this.props.updateThresholdField(fieldModel, changesData);
  }

  // Subtotal function chosen handler
  @action
  onSubtotalFunctionChanged(fieldModel: SettingsByRangeItemFieldModel, changesData) {
    this.props.updateThresholdFunction(fieldModel, changesData);
  }

  renderInputDynamicSubtotalThresholdCtrl(fieldModel: SettingsByRangeItemFieldModel, validateId) {
    const {itemData, fieldFunctions} = this.props;

    const validations = {
      presence: {
        presence: true
      }
    };
    return (
      <ValidateDropDown
        validations={validations}
        value={itemData.functionId}
        field={itemData.id}
        options={fieldFunctions}
        onChange={this.onSubtotalFunctionChanged.bind(this, fieldModel)}
        valueField="id"
        labelField="name"
      />
    );
  }

  renderInputDynamicThresholdCtrl(fieldModel: SettingsByRangeItemFieldModel) {
    const {itemData, dataTree, reportPartContent, fieldContainerName, localization, openPopup} = this.props;
    const context = {
      dataTree,
      reportPartContent,
      fieldContainerName
    };
    const validations = {
      presence: {
        presence: true
      }
    };
    return (
      <ValidateAddFieldSetting
        {...context}
        localization={localization}
        openPopup={openPopup}
        field={itemData.id}
        value={fieldModel.fieldName}
        fieldModel={fieldModel}
        validations={validations}
        onOk={this.onInputFieldChanged.bind(this, fieldModel)}
      />
    );
  }

  renderInputCtrl(dataType, source, valueKey, validateId, inputCtrlProps) {
    const {valueType} = this.props;

    prepareProps();
    return (
      <ValidateInputByDataType
        {...inputCtrlProps}
        type={dataType || 'text'}
        onChange={changeValue}/>
    );

    function changeValue(newVal) {
      source[valueKey] = newVal.value;
    }

    function prepareProps() {
      inputCtrlProps = assign(inputCtrlProps, {value: source[valueKey]});
      dataType === 'number' && source[valueKey] === '' && (inputCtrlProps = assign(inputCtrlProps, {value: null}));
      if (valueType === 'rangePercent' && dataType === 'number') {
        inputCtrlProps = assign(inputCtrlProps, {min: 0});
      } else if (dataType === 'select') {
        inputCtrlProps = assign(inputCtrlProps, Constants.BOOLEAN_OPTION);
      }

      if (dataType !== 'color') {
        inputCtrlProps = assign(inputCtrlProps, {validations: Constants.CONSTRAINTS, field: validateId});
      }

      // do not require empty input validation for null or blank inputs
      if (source.canBeBlank) {
        delete inputCtrlProps.validations;
      }
    }
  }

  isThresholdDynamic(thresholdValue) {
    return THRESHOLD_TYPES.DYNAMIC.value === thresholdValue;
  }

  isThresholdDynamicSubtotal(thresholdValue) {
    return THRESHOLD_TYPES.DYNAMIC_SUBTOTAL.value === thresholdValue;
  }

  getInputTypeByDataType(dataType, forcedDataType = null) {
    dataType = forcedDataType || dataType;
    switch (dataType) {
      case DATA_TYPE.TEXT:
        return 'text';
      case DATA_TYPE.MONEY:
      case DATA_TYPE.NUMERIC:
        return 'number';
      case DATA_TYPE.BOOLEAN:
        return 'select';
      case DATA_TYPE.DATETIME:
        return 'datetime';
      case DATA_TYPE.TIME:
        return 'time';
      default:
        return 'text';
    }
  }

  getDateTimeFormatData(dateTimeFormatDataFn) {
    return isFunction(dateTimeFormatDataFn) ? dateTimeFormatDataFn() : dateTimeFormatDataFn;
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/CommonSettings/Izenda.SettingsByRangeItem.js